import React from "react"
import { ChargingRulesTextFieldComponent } from "layouts/charging-rules/components/ChargingRulesTextFieldComponent.tsx"
import { ChargingRulesCheckboxComponent } from "layouts/charging-rules/components/ChargingRulesCheckboxComponent.tsx"
import { Box, Grid, Typography } from "@mui/material"
import { SelectChangeEvent } from "@mui/material/Select"
import { OreveSelect } from "components/OreveSelect.tsx"
import { getAlertActivationOptions, IsActive, } from "layouts/charging-rules/utils/utils.ts"
import { Rule, RuleType, } from "layouts/charging-rules/interface/Rules.ts"
import { useChargingRulesContext } from "layouts/charging-rules/context/useChargingRulesContext.ts"
import cautionIcon from "assets/svg/caution-icon.svg"
import ErrorIcon from "@mui/icons-material/Error"
import {
    DataCySelectOption,
    DataCySelectValue,
    GRID_SIZES,
    ruleTitlesTranslation,
} from "layouts/charging-rules/utils/const.ts"

interface ChargingRulesGlobalComponentProps {
    rule: Rule
    language?: "fr" | "en" | null
    isRfid?: boolean
}

const NoRuleAppliedMessage = () => (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <img src={cautionIcon} alt="caution-icon" />
        <Typography
            variant="label"
            sx={{ fontWeight: 700, color: "primary.main" }}
        >
            Aucune règle ne sera appliquée pour ce paramètre
        </Typography>
    </Box>
)

export const ChargingRulesGlobalComponent: React.FC<ChargingRulesGlobalComponentProps> = (
{
    rule,
    language = "fr",
    isRfid = false,
}) => {

    const {
        allDaysSelected,
        allHoursSelected,
        errorEnergy,
        setErrorEnergy,
        errorDuration,
        setErrorDuration,
        setIsRuleChanged,
        dispatch,
        schema
    } = useChargingRulesContext();

    const isActive = rule.isActive;
    const baseOptions = getAlertActivationOptions((language ?? "fr") as "fr" | "en", isRfid)
    const options =
        isActive === IsActive.COMPANY && !baseOptions.some(o => o.value === IsActive.COMPANY)
            ? [
                ...baseOptions,
                { value: IsActive.COMPANY, label: (language === "en" ? "Company rules" : "Règles d’entreprise") },
            ]
            : baseOptions

    const resetErrorStates = (ruleType: RuleType) => {
        // allDaysSelected/allHoursSelected are derived; we only reset number input errors here
        if (ruleType === RuleType.MaxRechargeEnergyOptions && errorEnergy) setErrorEnergy(false);
        if (ruleType === RuleType.MaxRechargeDurationOptions && errorDuration) setErrorDuration(false);
    };

    const getRuleTitle = (rule: Rule, language: "fr" | "en"): string | undefined =>
        ruleTitlesTranslation[language]?.[rule.type]

    const handleSelectChange = (event: SelectChangeEvent<unknown>) => {
        const newValue = event.target.value as IsActive;
        dispatch({ type: "setActive", ruleType: rule.type, isActive: newValue });
        if (newValue === IsActive.TRUE) {
            dispatch({ type: "activate", ruleType: rule.type });
        } else if (newValue === IsActive.FALSE) {
            dispatch({ type: "deactivate", ruleType: rule.type });
            resetErrorStates(rule.type);
        } else {
            dispatch({ type: "company", ruleType: rule.type });
        }
        setIsRuleChanged(true);
    };

    const renderRuleComponent = (isActive: IsActive) => {
        switch (rule.type) {
            case RuleType.DOWBlocked:
            case RuleType.HoursBlocked:
                return (
                    <>
                        <ChargingRulesCheckboxComponent
                            ruleOptions={ isActive === IsActive.COMPANY ? rule.defaultOptions?? {} : rule.options ?? {}}
                            language={"fr"}
                            ruleType={rule.type}
                        />
                        {allDaysSelected && rule.type === RuleType.DOWBlocked && (
                            <Box
                                sx={{display: "flex", flexDirection: "row", mt: "1rem"}}
                                data-cy={"nav-charging-rules__DOWBlocked--error-message"}
                            >
                                <ErrorIcon fontSize="small" color="error" />
                                <Typography variant="bodySmall" sx={{color: "error.main", mt: "0.2rem", ml:"0.5rem", fontWeight: 500 }}>
                                    Vous ne pouvez pas sélectionner tous les jours.
                                </Typography>
                            </Box>
                        )}
                        {allHoursSelected && rule.type === RuleType.HoursBlocked && (
                            <Box
                                sx={{display: "flex", flexDirection: "row", mt: "1rem"}}
                                data-cy={"nav-charging-rules__HoursBlocked--error-message"}
                            >
                                <ErrorIcon fontSize="small" color="error" />
                                <Typography variant="bodySmall" sx={{color: "error.main", mt: "0.2rem", ml:"0.5rem", fontWeight: 500 }}>
                                    Vous ne pouvez pas sélectionner tous les créneaux horaires.
                                </Typography>
                            </Box>
                        )}
                    </>
                );
                case RuleType.MaxRechargeDurationOptions: {
                    return (
                        <ChargingRulesTextFieldComponent
                            ruleOptions={{ value: isActive === IsActive.COMPANY ? rule.defaultValue ?? null : rule.value ?? null }}
                            language="fr"
                            ruleType={rule.type}
                            min={5}
                            max={1440}
                        />
                    );
                }
                case RuleType.MaxRechargeEnergyOptions: {
                    return (
                        <ChargingRulesTextFieldComponent
                            ruleOptions={{ value: isActive === IsActive.COMPANY ? rule.defaultValue ?? null : rule.value ?? null }}
                            language="fr"
                            ruleType={rule.type}
                            min={5}
                            max={400}
                        />
                    );
                }
            }
        }
    return (
        <Grid
            container
            spacing={3}
            alignItems="center"
            sx={{
                width: "100%",
                height: "100%",
                flexWrap: { xs: "wrap", md: "nowrap" },
            }}
        >
            <Grid />
            <Grid
                size={GRID_SIZES.TITLE}
                component="div"
                sx={{ mb: { xs: 2, md: 0 } }}
            >
                <Typography
                    variant="labelLarge"
                    sx={{
                        color: "grey.700",
                        fontWeight: 400,
                        minWidth: "fit-content",
                    }}
                >
                    {getRuleTitle(rule, language as "fr" | "en")}&nbsp;:
                </Typography>
            </Grid>
            <Grid size={GRID_SIZES.SELECT}>
                <OreveSelect
                    value={isActive}
                    options={options}
                    onChange={(newValue) =>
                        handleSelectChange({
                            target: { value: newValue },
                        } as SelectChangeEvent)
                    }
                    dataCyParent={DataCySelectValue(rule.type)}
                    dataCyOption={DataCySelectOption(rule.type)}
                />
            </Grid>
            <Grid key={JSON.stringify(schema)} size={GRID_SIZES.CONTENT} sx={{ mt: { xs: 2, md: 0 } }}>
                <Box key={1}>
                    {isActive === IsActive.TRUE && renderRuleComponent(isActive)}
                    {isActive === IsActive.FALSE && <NoRuleAppliedMessage />}
                    {isActive === IsActive.COMPANY && <Box sx={{ opacity: 0.5, pointerEvents: "none" }}>
                        {renderRuleComponent(isActive)}
                    </Box>}
                </Box>
            </Grid>
            <Grid />
        </Grid>
    )
};